import styled, { css } from "styled-components";

import { colors } from "@src/packages/chime-ui/system";

const paragraphMixin = css`
  padding: 0;
  margin: 0;

  a {
    color: ${colors.notBlack};

    &:hover,
    &:focus {
      outline: none;
      font-weight: 700;
    }

    &:active,
    &:visited {
      color: ${colors.notBlack};
    }
  }
`;

const Paragraph = styled.p`
  ${paragraphMixin}
`;

const textTypes = {
  small: {
    fontSize: "12px",
    lineHeight: "17px",
  },
  standard: {
    fontSize: "16px",
    lineHeight: "22px",
  },
  large: {
    fontWeight: "700",
    fontSize: "38px",
    lineHeight: "51px",
  },
};

interface TextProps {
  color?: string;
  size: keyof typeof textTypes;
  bold?: boolean;
}

const textMixin = css<TextProps>`
  color: ${({ color }) => color || "inherit"};
  font-size: ${({ size }) => textTypes[size]?.fontSize || "auto"};
  line-height: ${({ size }) => textTypes[size]?.lineHeight || "auto"};
  font-weight: ${({ bold }) => (bold ? 700 : "inherit")};
  word-break: break-word;
`;

export const Text = styled.span<TextProps>`
  ${textMixin}
`;

export const TextBlock = styled.div<TextProps>`
  ${textMixin}
`;

const titleTypes = {
  small: {
    fontSize: "21px",
    lineHeight: "28px",
  },
  medium: {
    fontSize: "30px",
    lineHeight: "38px",
  },
  xLarge: {
    fontSize: "51px",
    lineHeight: "64px",
  },
};

interface TitleProps {
  size: keyof typeof titleTypes;
}

export const Title = styled.div<TitleProps>`
  font-weight: 700;
  font-size: ${({ size }) => titleTypes[size]?.fontSize || "auto"};
  line-height: ${({ size }) => titleTypes[size]?.lineHeight || "auto"};
  color: ${({ color }) => color || "inherit"};
`;

const CenterAligned = styled(Paragraph)`
  text-align: center;
`;

export const SmallTitle = styled.h1`
  ${paragraphMixin}
  font-weight: 700;
  font-size: 21px;
  line-height: 28px;
  margin-bottom: 0.5rem;
`;

export const SmallTitleSpaced = styled(SmallTitle)`
  margin-bottom: 0.5rem;
`;

export const CenteredSmallTitle = styled(SmallTitle)`
  text-align: center;
`;

export const XLargeAmount = styled(CenterAligned)`
  font-weight: 700;
  font-size: 51px;
  line-height: 64px;
`;

const standardTextMixin = css`
  ${paragraphMixin}
  font-size: 16px;
  line-height: 22px;
`;

export const StandardText = styled.p`
  ${standardTextMixin}
`;

export const StandardTextSpaced = styled(StandardText)`
  margin-bottom: 0.5rem;
`;

export const ItalicText = styled(Text)`
  font-style: italic;
`;

export const StrongText = styled.h2`
  ${standardTextMixin}
  font-weight: 700;
`;

export const StrongTextSpaced = styled(StrongText)`
  margin-top: 1rem;
  margin-bottom: 0.5rem;
`;

export const StrongTextInline = styled(Text)`
  font-weight: 700;
`;

interface TextProps {
  color?: string;
}

export const SmallText = styled(Paragraph)<TextProps>`
  font-size: 12px;
  line-height: 17px;
  color: ${({ color }) => (color ? color : colors.darkGrey)};
`;

export const FinePrint = styled.small`
  font-size: 12px;
  text-align: left;
  line-height: 17px;
`;

export const InfoCall = styled(CenterAligned)`
  color: ${({ color }) => (color ? color : colors.darkGrey)};
  font-size: 12px;
  line-height: 17px;
  text-decoration-line: underline;
  margin: 0 auto 0.5rem;
  background: none;
  outline: none;
  border: none;
  cursor: pointer;
  display: block;
`;
